import { defineStore } from 'pinia';
import { ref, shallowRef, computed } from 'vue';
import type { MemberProductMapProduct } from '~~/shared/globalTypes/product';

/**
 * Store for managing tabs and grouping member products in the STC experience.
 */
export const useShopTheCollectionTabsStore = defineStore('shopTheCollectionTabs', () => {
  const productData = shallowRef<Product | null>(null);
  const activeTab = ref<string>('');

  const tabs = computed(() => {
    return productData.value?.detail?.orderedMasterGroupList || [];
  });

  const membersByGroup = computed(() => {
    const groupMap = productData.value?.detail?.memberDisplayGroupsMap || {};
    const productMap = productData.value?.relationships?.memberProductMap || {};

    return tabs.value.reduce(
      (acc, groupName) => {
        const memberIds = groupMap[groupName] || [];
        // Filter out unavailable members
        acc[groupName] = memberIds
          .map((id) => productMap[id])
          .filter((member) => member && member.availability?.available) as MemberProductMapProduct[];
        return acc;
      },
      {} as Record<string, MemberProductMapProduct[]>,
    );
  });

  const activeTabMembers = computed(() => {
    return membersByGroup.value[activeTab.value] || [];
  });

  const activeTabMemberIds = computed(() => {
    return activeTabMembers.value.map((member) => member.id);
  });

  const memberCountByGroup = computed(() => {
    return Object.keys(membersByGroup.value).reduce(
      (acc, groupName) => {
        acc[groupName] = membersByGroup.value[groupName]?.length || 0;
        return acc;
      },
      {} as Record<string, number>,
    );
  });

  const setActiveTab = (tabName?: string) => {
    if (tabName && tabs.value.includes(tabName)) {
      activeTab.value = tabName;
    } else if (tabs.value.length > 0) {
      activeTab.value = tabs.value[0] || '';
    }
  };

  const init = (data: Product) => {
    productData.value = data;
    if (!activeTab.value && tabs.value.length > 0) {
      setActiveTab();
    }
  };

  return {
    activeTab,
    tabs,
    membersByGroup,
    activeTabMembers,
    activeTabMemberIds,
    memberCountByGroup,
    setActiveTab,
    init,
  };
});
